import React, {useEffect, useRef} from 'react';
import {
    Flex,
    Select,
    Link,
    CheckBox,
    Text,
    Separator
} from '@priority-software/priority-style-react';
import {setExcelTemplate, openTemplateDesign} from '../../../_shared/GWTBridge/invoke/procMain';
import styles from '../styles/ReportsDialog.module.scss';
import {selectedOptionsMap} from '../consts/reportOptions';
import {ReportOptions} from './ReportsOptions';
import texts from '../texts';
import classNames from 'classnames';
import {featurePopoverRef} from '../../FeaturePopover/FeaturePopover.root';
import {featuresIDs} from '../../FeaturePopover/constants/map';
import {ExcelMappedTemplate, ReportDialogOption} from '../types';

type Format = {
    value: string;
    label: string;
    isSelected: boolean;
};

interface IReportDialogContentProps {
    config: ReportDialogOption[];
    selectedOption: ReportDialogOption;
    chooseOption: (option: ReportDialogOption) => void;
    formats: Format[];
    selectedFormat: Format;
    setSelectedFormat: (format: Format) => void;
    excelTemplates: Array<ExcelMappedTemplate>;
    selectedExcelTemplate: ExcelMappedTemplate;
    setSelectedExcelTemplate: (tmpl: ExcelMappedTemplate) => void;
    isLockedExcel: boolean;
    setIsLockedExcel: React.Dispatch<React.SetStateAction<boolean>>;
    isPdf: boolean;
    setIsPdf: React.Dispatch<React.SetStateAction<boolean>>;
    hasword: string;
    closeDialog: () => void;
}

export const ReportDialogContent: React.FC<IReportDialogContentProps> = ({
    config,
    selectedOption,
    chooseOption,
    formats,
    selectedFormat,
    setSelectedFormat,
    excelTemplates,
    selectedExcelTemplate,
    setSelectedExcelTemplate,
    isLockedExcel,
    setIsLockedExcel,
    isPdf,
    setIsPdf,
    hasword,
    closeDialog
}) => {
    const showFormats = selectedOption?.formats && formats?.length;
    const showExcelTemplates = selectedOption?.excelTemplates && excelTemplates?.length;
    const excelTemplatesRef = useRef(null);

    useEffect(() => {
        if (
            excelTemplatesRef &&
            excelTemplatesRef.current &&
            selectedOption.mode === selectedOptionsMap.excel
        ) {
            setTimeout(() => {
                featurePopoverRef?.current?.showFeaturePopover(
                    featuresIDs.excelTemplates,
                    excelTemplatesRef?.current
                );
            }, 400);
        } else {
            featurePopoverRef?.current?.handleClose(true);
        }
    }, [selectedOption]);

    const openDesignDialog = () => {
        if (selectedOption.mode === selectedOptionsMap.excel) {
            closeDialog();
        }
        openTemplateDesign(selectedOption.mode === selectedOptionsMap.excel, hasword === '1');
    };

    const isCheckboxChecked = () => {
        return (
            (selectedOption.mode === selectedOptionsMap.excel && isLockedExcel) ||
            ((selectedOption.mode === selectedOptionsMap.report ||
                selectedOption.mode === selectedOptionsMap.table) &&
                isPdf)
        );
    };

    const onCheckBoxChangeHandler = () => {
        if (
            selectedOption.mode === selectedOptionsMap.report ||
            selectedOption.mode === selectedOptionsMap.table
        ) {
            setIsPdf(prevValue => !prevValue);
        } else if (selectedOption.mode === selectedOptionsMap.excel) {
            setIsLockedExcel(prevValue => !prevValue);
        }
    };

    const onFormatChooseHandler = item => {
        setSelectedFormat(item);
    };

    const onExcelChooseHandler = item => {
        setSelectedExcelTemplate(item);
        setExcelTemplate(item.value);
    };

    return (
        <Flex direction="column" componentClasses={styles.reportDialogContent}>
            <ReportOptions
                options={config}
                selectedOption={selectedOption}
                chooseOption={chooseOption}
            />
            <Flex justifyContent="flex-start" componentClasses={styles.selectFormatWrapper}>
                {showFormats ? (
                    <Select
                        componentClasses={classNames(
                            styles.formatDropdown,
                            styles.horizontalMargin
                        )}
                        options={formats}
                        defaultValue={selectedFormat}
                        onChange={onFormatChooseHandler}
                    />
                ) : null}
                {showExcelTemplates ? (
                    <>
                        <div ref={excelTemplatesRef} />
                        <Select
                            componentClasses={classNames(
                                styles.formatDropdown,
                                styles.horizontalMargin,
                                {
                                    [styles.disabledTemplateInput]: selectedExcelTemplate?.isOld
                                }
                            )}
                            options={excelTemplates}
                            defaultValue={selectedExcelTemplate}
                            onChange={onExcelChooseHandler}
                        />
                    </>
                ) : null}
                {selectedOption.design && (
                    <Link componentClasses={styles.horizontalMargin} onClick={openDesignDialog}>
                        {selectedOption.design}
                    </Link>
                )}
                {!!selectedOption.checkbox && hasword !== '1' ? (
                    <>
                        {selectedOption.design ? (
                            <Separator
                                align="horizontal"
                                height={30}
                                componentClasses={styles.horizontalMargin}
                            />
                        ) : null}
                        <CheckBox
                            checked={isCheckboxChecked()}
                            onChange={onCheckBoxChangeHandler}
                            componentClasses={styles.horizontalMargin}
                        >
                            {selectedOption.checkbox}
                        </CheckBox>
                    </>
                ) : null}
            </Flex>
            <Text
                componentClasses={classNames(styles.oldTemplatesWarn, {
                    [styles.visible]: showExcelTemplates && selectedExcelTemplate?.isOld
                })}
                skin={'label-2'}
                textColor={'red'}
            >
                {texts.oldTemplateWarn}
            </Text>
        </Flex>
    );
};
